import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { Token as TokenIcon } from '@mui/icons-material';
import { formatCredits, dollarsToCredits, CREDITS_PER_DOLLAR } from '../../utils/formatters';

interface CreditPointsDisplayProps {
  /** Amount in dollars (will be converted to credit points) */
  dollars: number | null | undefined;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Show label text */
  showLabel?: boolean;
  /** Use short label "CP" instead of "Credit Points" */
  shortLabel?: boolean;
  /** Text color */
  color?: string;
  /** Show tooltip with conversion info */
  showTooltip?: boolean;
}

/**
 * Display component for Credit Points
 * Converts dollar amounts to credit points (1 USD = 20,000 CP)
 */
const CreditPointsDisplay: React.FC<CreditPointsDisplayProps> = ({
  dollars,
  size = 'medium',
  showLabel = true,
  shortLabel = false,
  color,
  showTooltip = true,
}) => {
  const credits = dollarsToCredits(dollars);
  const formattedCredits = formatCredits(dollars);

  const sizeConfig = {
    small: { iconSize: 16, fontSize: '0.875rem', gap: 0.5 },
    medium: { iconSize: 20, fontSize: '1rem', gap: 0.75 },
    large: { iconSize: 28, fontSize: '1.5rem', gap: 1 },
  };

  const { iconSize, fontSize, gap } = sizeConfig[size];

  const content = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap,
        color: color || 'inherit',
      }}
    >
      <TokenIcon
        sx={{
          fontSize: iconSize,
          color: color || 'primary.main',
        }}
      />
      <Typography
        component="span"
        sx={{
          fontSize,
          fontWeight: 600,
          color: color || 'inherit',
        }}
      >
        {formattedCredits}
        {showLabel && (
          <Typography
            component="span"
            sx={{
              fontSize,
              fontWeight: 400,
              ml: 0.5,
              color: color || 'text.secondary',
            }}
          >
            {shortLabel ? 'CP' : 'Credit Points'}
          </Typography>
        )}
      </Typography>
    </Box>
  );

  if (showTooltip) {
    return (
      <Tooltip
        title={`${formattedCredits} Credit Points (1 USD = ${CREDITS_PER_DOLLAR.toLocaleString()} CP)`}
        arrow
      >
        {content}
      </Tooltip>
    );
  }

  return content;
};

export default CreditPointsDisplay;
